import { useCallback, useEffect, useRef, useState } from "react";
import { PlaybackQuality, PlaybackSegment } from "@situate/contracts/playback-segment";
import {
  ulidBytesToString,
  unixNanosecondsToRfc3339,
} from "@situate/contracts/fused-area-frame-codec";
import {
  PLAYBACK_SAMPLE_INTERVAL_MS,
  type PlaybackManifestResponse,
  type PlaybackManifestSegment,
  type PlaybackQualityName,
} from "@situate/contracts/runtime";
import { playbackManifestResponseSchema } from "@situate/contracts/runtime-zod";
import { useRuntimeHttpClient } from "@/features/runtime-session/hooks/use-runtime-http-client";
import type { AreaFrameUpdateObject } from "@situate/contracts/runtime";
import { ApiError } from "@/shared/api/api-error";

const MANIFEST_PATH = "/v1/playback/manifest";
const SEGMENT_PATH = "/v1/playback/segment";
const MAX_BUFFERED_SEGMENTS = 6;
const PREVIOUS_BUFFERED_SEGMENT_COUNT = 1;
const NEXT_BUFFERED_SEGMENT_COUNT = 2;

export interface PlaybackFrameData {
  area_id: string;
  fused_frame_id: string;
  frame_time: string;
  reference_frame_id: string;
  objects: AreaFrameUpdateObject[];
}

interface BufferedSegment {
  index: number;
  frames: PlaybackFrameData[];
  lastAccessed: number;
}

interface PendingSegment {
  controller: AbortController;
  promise: Promise<boolean>;
}

export interface PlaybackBufferError {
  kind: "manifest" | "segment";
  message: string;
  segmentIndex?: number;
  oversized?: boolean;
}

export interface PlaybackBufferState {
  manifest: PlaybackManifestResponse | null;
  loadedSegmentIndexes: ReadonlySet<number>;
  loadingSegmentIndexes: ReadonlySet<number>;
  error: PlaybackBufferError | null;
  ensureSegment: (index: number) => Promise<boolean>;
  ensureSegmentWindow: (currentIndex: number) => void;
  retrySegment: (index: number) => Promise<boolean>;
  retainSegmentWindow: (startIndex: number, endIndex: number) => void;
  getBufferedFrame: (timestamp: number) => PlaybackFrameData | undefined;
  getBucketedBufferedFrame: (timestamp: number) => PlaybackFrameData | undefined;
  getBufferedFrameWindow: (timestamp: number, frameCount: number) => PlaybackFrameData[];
}

interface PlaybackBufferScope {
  deploymentId: string;
  areaId: string;
  startTime: string;
  endTime: string;
  quality: PlaybackQualityName;
  generation: number;
}

const EMPTY_SET: ReadonlySet<number> = new Set();

function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === "AbortError") ||
    (error instanceof Error && error.name === "AbortError")
  );
}

function toTimestamp(value: { unixNanos: string } | undefined, fieldName: string): string {
  if (value === undefined) throw new Error(`${fieldName} is required`);
  return unixNanosecondsToRfc3339(value.unixNanos);
}

function toQualityEnum(quality: PlaybackQualityName): PlaybackQuality {
  switch (quality) {
    case "best":
      return PlaybackQuality.BEST;
    case "high":
      return PlaybackQuality.HIGH;
    case "balanced":
      return PlaybackQuality.BALANCED;
    case "low":
      return PlaybackQuality.LOW;
    case "very_low":
      return PlaybackQuality.VERY_LOW;
  }
}

function toPlaybackFrame(
  areaId: string,
  frame: {
    fusedFrameId?: { value: Uint8Array };
    frameTime?: { unixNanos: string };
    referenceFrameId: string;
    objects: Array<{
      objectId?: { value: Uint8Array };
      trackId: string;
      position?: { x: number; y: number };
    }>;
  }
): PlaybackFrameData {
  if (frame.referenceFrameId.length === 0) {
    throw new Error("Playback frame reference_frame_id is required");
  }
  if (frame.fusedFrameId === undefined) {
    throw new Error("Playback frame fused_frame_id is required");
  }

  return {
    area_id: areaId,
    fused_frame_id: ulidBytesToString(frame.fusedFrameId.value),
    frame_time: toTimestamp(frame.frameTime, "Playback frame frame_time"),
    reference_frame_id: frame.referenceFrameId,
    objects: frame.objects.map((object) => {
      if (object.objectId === undefined) throw new Error("Playback object object_id is required");
      if (object.position === undefined) throw new Error("Playback object position is required");
      if (!Number.isFinite(object.position.x) || !Number.isFinite(object.position.y)) {
        throw new Error("Playback object position must be finite");
      }

      return {
        object_id: ulidBytesToString(object.objectId.value),
        ...(object.trackId.length > 0 ? { track_id: object.trackId } : {}),
        position: { x: object.position.x, y: object.position.y },
      };
    }),
  };
}

function validateManifest(
  response: PlaybackManifestResponse,
  scope: PlaybackBufferScope
): PlaybackManifestResponse {
  if (response.area_id !== scope.areaId)
    throw new Error("Playback manifest area does not match the selected area");
  if (response.playback_quality !== scope.quality) {
    throw new Error("Playback manifest quality does not match the requested quality");
  }
  if (response.sample_interval_ms !== PLAYBACK_SAMPLE_INTERVAL_MS[scope.quality]) {
    throw new Error("Playback manifest sample interval does not match the requested quality");
  }
  if (
    response.requested_start_time !== scope.startTime ||
    response.requested_end_time !== scope.endTime
  ) {
    throw new Error("Playback manifest range does not match the requested range");
  }

  let previousIndex = -1;
  for (const segment of response.segments) {
    if (segment.index <= previousIndex)
      throw new Error("Playback manifest segment indexes are not ordered");
    if (segment.index !== previousIndex + 1)
      throw new Error("Playback manifest segment indexes are not contiguous");
    if (Date.parse(segment.end_time) <= Date.parse(segment.start_time)) {
      throw new Error("Playback manifest contains an invalid segment range");
    }
    if (segment.frame_count > segment.expected_sample_count) {
      throw new Error("Playback manifest frame count exceeds its expected sample count");
    }
    previousIndex = segment.index;
  }

  return response;
}

function findDescriptor(
  manifest: PlaybackManifestResponse | null,
  index: number
): PlaybackManifestSegment | undefined {
  return manifest?.segments.find((segment) => segment.index === index);
}

function findSegmentForTimestamp(
  manifest: PlaybackManifestResponse | null,
  timestamp: number
): PlaybackManifestSegment | undefined {
  return manifest?.segments.find((segment) => {
    const start = Date.parse(segment.start_time);
    const end = Date.parse(segment.end_time);
    return timestamp >= start && timestamp < end;
  });
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function decodeSegment(
  bytes: ArrayBuffer,
  descriptor: PlaybackManifestSegment,
  scope: PlaybackBufferScope
): PlaybackFrameData[] {
  const segment = PlaybackSegment.fromBinary(new Uint8Array(bytes));
  if (segment.schemaVersion !== 1)
    throw new Error(`Unsupported playback segment schema version: ${segment.schemaVersion}`);
  if (segment.areaId !== scope.areaId)
    throw new Error("Playback segment area does not match the selected area");
  if (segment.quality !== toQualityEnum(scope.quality)) {
    throw new Error("Playback segment quality does not match the requested quality");
  }
  if (segment.sampleIntervalMs !== PLAYBACK_SAMPLE_INTERVAL_MS[scope.quality]) {
    throw new Error("Playback segment sample interval does not match the requested quality");
  }

  const segmentStart = Date.parse(descriptor.start_time);
  const segmentEnd = Date.parse(descriptor.end_time);
  const envelopeStart = Date.parse(toTimestamp(segment.startTime, "Playback segment start_time"));
  const envelopeEnd = Date.parse(toTimestamp(segment.endTime, "Playback segment end_time"));
  if (envelopeStart !== segmentStart || envelopeEnd !== segmentEnd) {
    throw new Error("Playback segment bounds do not match its manifest descriptor");
  }

  const frames = segment.frames.map((frame) => toPlaybackFrame(scope.areaId, frame));
  for (const frame of frames) {
    const frameTime = Date.parse(frame.frame_time);
    if (frameTime < segmentStart || frameTime >= segmentEnd) {
      throw new Error("Playback segment contains a frame outside its manifest descriptor");
    }
  }
  return frames.sort((left, right) => Date.parse(left.frame_time) - Date.parse(right.frame_time));
}

export function usePlaybackBuffer({
  deploymentId,
  areaId,
  startTime,
  endTime,
  quality,
}: {
  deploymentId: string;
  areaId: string;
  startTime: string;
  endTime: string;
  quality: PlaybackQualityName;
}): PlaybackBufferState {
  const runtime = useRuntimeHttpClient();
  const client = runtime.client;
  const hasManifestGrant = runtime.operationGrants.some(
    (grant) => grant.operation_id === "playback_manifest"
  );
  const hasSegmentGrant = runtime.operationGrants.some(
    (grant) => grant.operation_id === "playback_segment"
  );
  const [manifest, setManifest] = useState<PlaybackManifestResponse | null>(null);
  const [loadedSegmentIndexes, setLoadedSegmentIndexes] = useState<ReadonlySet<number>>(EMPTY_SET);
  const [loadingSegmentIndexes, setLoadingSegmentIndexes] =
    useState<ReadonlySet<number>>(EMPTY_SET);
  const [error, setError] = useState<PlaybackBufferError | null>(null);
  const cacheRef = useRef(new Map<number, BufferedSegment>());
  const pendingRef = useRef(new Map<number, PendingSegment>());
  const scopeRef = useRef<PlaybackBufferScope>({
    deploymentId,
    areaId,
    startTime,
    endTime,
    quality,
    generation: 0,
  });

  useEffect(() => {
    const scope: PlaybackBufferScope = {
      deploymentId,
      areaId,
      startTime,
      endTime,
      quality,
      generation: scopeRef.current.generation + 1,
    };
    scopeRef.current = scope;
    for (const pending of pendingRef.current.values()) pending.controller.abort();
    pendingRef.current.clear();
    cacheRef.current.clear();
    setManifest(null);
    setLoadedSegmentIndexes(new Set());
    setLoadingSegmentIndexes(new Set());
    setError(null);

    if (
      areaId.length === 0 ||
      client === null ||
      runtime.deploymentId !== deploymentId ||
      !hasManifestGrant ||
      !hasSegmentGrant
    ) {
      return;
    }

    const controller = new AbortController();
    void client
      .post<unknown>(
        MANIFEST_PATH,
        {
          area_id: areaId,
          start_time: startTime,
          end_time: endTime,
          quality,
        },
        { signal: controller.signal }
      )
      .then((response) => playbackManifestResponseSchema.parse(response))
      .then((response) => validateManifest(response, scope))
      .then((response) => {
        if (scopeRef.current.generation !== scope.generation) return;
        setManifest(response);
      })
      .catch((requestError: unknown) => {
        if (controller.signal.aborted || scopeRef.current.generation !== scope.generation) return;
        setError({
          kind: "manifest",
          message: getErrorMessage(requestError, "Unable to load playback manifest"),
        });
      });

    return () => controller.abort();
  }, [
    areaId,
    client,
    deploymentId,
    endTime,
    hasManifestGrant,
    hasSegmentGrant,
    quality,
    runtime.deploymentId,
    startTime,
  ]);

  const getBufferedFrame = useCallback(
    (timestamp: number): PlaybackFrameData | undefined => {
      const descriptor = findSegmentForTimestamp(manifest, timestamp);
      if (!descriptor) return undefined;
      const segment = cacheRef.current.get(descriptor.index);
      if (!segment) return undefined;
      segment.lastAccessed = performance.now();
      return segment.frames.find((frame) => Date.parse(frame.frame_time) === timestamp);
    },
    [manifest]
  );

  const getBucketedBufferedFrame = useCallback(
    (timestamp: number): PlaybackFrameData | undefined => {
      const descriptor = findSegmentForTimestamp(manifest, timestamp);
      if (!descriptor || manifest === null) return undefined;
      const segment = cacheRef.current.get(descriptor.index);
      if (!segment || segment.frames.length === 0) return undefined;

      const bucketStart =
        Math.floor(timestamp / manifest.sample_interval_ms) * manifest.sample_interval_ms;
      const bucketEnd = bucketStart + manifest.sample_interval_ms;
      segment.lastAccessed = performance.now();
      return segment.frames.find((frame) => {
        const frameTimestamp = Date.parse(frame.frame_time);
        return (
          frameTimestamp >= bucketStart && frameTimestamp < bucketEnd && frameTimestamp <= timestamp
        );
      });
    },
    [manifest]
  );

  const getBufferedFrameWindow = useCallback(
    (timestamp: number, frameCount: number): PlaybackFrameData[] => {
      if (manifest === null || frameCount <= 0) return [];
      const descriptor = findSegmentForTimestamp(manifest, timestamp);
      if (!descriptor) return [];
      const segment = cacheRef.current.get(descriptor.index);
      if (!segment) return [];
      segment.lastAccessed = performance.now();
      return segment.frames
        .filter((frame) => Date.parse(frame.frame_time) <= timestamp)
        .slice(-frameCount);
    },
    [manifest]
  );

  const ensureSegment = useCallback(
    async (index: number): Promise<boolean> => {
      const scope = scopeRef.current;
      if (
        scope.deploymentId !== deploymentId ||
        scope.areaId !== areaId ||
        scope.startTime !== startTime ||
        scope.endTime !== endTime ||
        scope.quality !== quality
      ) {
        return false;
      }
      const descriptor = findDescriptor(manifest, index);
      if (!descriptor || manifest === null) return false;
      if (descriptor.availability === "empty") return true;
      if (cacheRef.current.has(index)) {
        const cached = cacheRef.current.get(index);
        if (cached) cached.lastAccessed = performance.now();
        return true;
      }
      const pending = pendingRef.current.get(index);
      if (pending) return pending.promise;
      if (client === null) return false;

      const controller = new AbortController();
      const generation = scope.generation;
      setLoadingSegmentIndexes((current) => new Set(current).add(index));
      const promise = client
        .postBinary(
          SEGMENT_PATH,
          {
            area_id: areaId,
            start_time: descriptor.start_time,
            end_time: descriptor.end_time,
            quality,
          },
          { signal: controller.signal }
        )
        .then((bytes) => {
          if (scopeRef.current.generation !== generation) return false;
          const frames = decodeSegment(bytes, descriptor, scope);
          cacheRef.current.set(index, { index, frames, lastAccessed: performance.now() });
          while (cacheRef.current.size > MAX_BUFFERED_SEGMENTS) {
            const oldest = [...cacheRef.current.values()].reduce((left, right) =>
              left.lastAccessed <= right.lastAccessed ? left : right
            );
            cacheRef.current.delete(oldest.index);
          }
          setLoadedSegmentIndexes(new Set(cacheRef.current.keys()));
          setError((current) =>
            current?.kind === "segment" && current.segmentIndex === index ? null : current
          );
          return true;
        })
        .catch((requestError: unknown) => {
          if (isAbortError(requestError) || scopeRef.current.generation !== generation)
            return false;
          setError({
            kind: "segment",
            segmentIndex: index,
            message: getErrorMessage(requestError, "Unable to load playback segment"),
            oversized: requestError instanceof ApiError && requestError.statusCode === 413,
          });
          return false;
        })
        .finally(() => {
          if (scopeRef.current.generation !== generation) return;
          pendingRef.current.delete(index);
          setLoadingSegmentIndexes((current) => {
            const next = new Set(current);
            next.delete(index);
            return next;
          });
        });

      pendingRef.current.set(index, { controller, promise });
      return promise;
    },
    [areaId, client, deploymentId, endTime, manifest, quality, startTime]
  );

  const retrySegment = useCallback((index: number) => ensureSegment(index), [ensureSegment]);

  const retainSegmentWindow = useCallback((startIndex: number, endIndex: number): void => {
    for (const [index, pending] of pendingRef.current) {
      if (index < startIndex || index > endIndex) {
        pending.controller.abort();
        pendingRef.current.delete(index);
      }
    }
    for (const index of cacheRef.current.keys()) {
      if (index < startIndex || index > endIndex) cacheRef.current.delete(index);
    }
    setLoadedSegmentIndexes(new Set(cacheRef.current.keys()));
    setLoadingSegmentIndexes(
      (current) => new Set([...current].filter((index) => index >= startIndex && index <= endIndex))
    );
  }, []);

  const ensureSegmentWindow = useCallback(
    (currentIndex: number): void => {
      if (manifest === null) return;

      const startIndex = Math.max(0, currentIndex - PREVIOUS_BUFFERED_SEGMENT_COUNT);
      const endIndex = Math.min(
        manifest.segments.length - 1,
        currentIndex + NEXT_BUFFERED_SEGMENT_COUNT
      );
      retainSegmentWindow(startIndex, endIndex);

      for (let index = startIndex; index <= endIndex; index += 1) {
        const segment = findDescriptor(manifest, index);
        if (segment?.availability !== "empty") void ensureSegment(index);
      }
    },
    [ensureSegment, manifest, retainSegmentWindow]
  );

  useEffect(
    () => () => {
      scopeRef.current = { ...scopeRef.current, generation: scopeRef.current.generation + 1 };
      for (const pending of pendingRef.current.values()) pending.controller.abort();
    },
    []
  );

  return {
    manifest,
    loadedSegmentIndexes,
    loadingSegmentIndexes,
    error,
    ensureSegment,
    ensureSegmentWindow,
    retrySegment,
    retainSegmentWindow,
    getBufferedFrame,
    getBucketedBufferedFrame,
    getBufferedFrameWindow,
  };
}
